import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Save, ArrowLeft, Eye, EyeOff, ExternalLink, Calendar, History, Image as ImageIcon,
  CheckCircle2, Clock, X, RotateCcw,
} from "lucide-react";
import RichTextEditor from "../../components/cms/RichTextEditor";
import MediaPicker from "../../components/cms/MediaPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const F = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-slate-700">{label}</span>
    {hint && <span className="block text-xs text-slate-500 mt-0.5">{hint}</span>}
    <div className="mt-1.5">{children}</div>
  </label>
);
const I = (p) => <input {...p} className={`w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/40 ${p.className || ""}`} />;
const TA = (p) => <textarea {...p} className={`w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/40 ${p.className || ""}`} />;

const Badge = ({ status }) => {
  const map = {
    draft: { cls: "bg-slate-100 text-slate-600", label: "Draft", Icon: EyeOff },
    scheduled: { cls: "bg-amber-100 text-amber-700", label: "Scheduled", Icon: Clock },
    published: { cls: "bg-prepify-100 text-prepify-700", label: "Published", Icon: CheckCircle2 },
  };
  const { cls, label, Icon } = map[status] || map.draft;
  return <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}><Icon size={12} /> {label}</span>;
};

const AUTOSAVE_DELAY_MS = 2500;

const BlogEditor = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [autoSavedAt, setAutoSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [users, setUsers] = useState([]);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaOnPick, setMediaOnPick] = useState(null); // callback with selected url
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [showRevisions, setShowRevisions] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const autosaveTimer = useRef(null);

  useEffect(() => {
    axios.get(`${API}/cms/blog/${id}`).then((r) => setPost(r.data)).catch(() => { toast.error("Post not found"); nav("/app/website/blog"); });
  }, [id, nav]);

  useEffect(() => {
    axios.get(`${API}/cms/blog-categories`).then((r) => setCategories(r.data)).catch(() => {});
    axios.get(`${API}/cms/blog-tags`).then((r) => setTags(r.data)).catch(() => {});
    // Authors = users who can edit content
    axios.get(`${API}/users`).then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  const buildPayload = useCallback((p) => ({
    title: p.title,
    slug: p.slug,
    excerpt: p.excerpt,
    content_html: p.content_html,
    cover_image_url: p.cover_image_url,
    tags: p.tags,
    category: p.category,
    category_ids: p.category_ids || [],
    tag_ids: p.tag_ids || [],
    author_override_name: p.author_override_name || "",
    seo_title: p.seo_title,
    seo_description: p.seo_description,
    seo_og_image: p.seo_og_image,
  }), []);

  const saveNow = useCallback(async (silent = false) => {
    if (!post) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/cms/blog/${id}`, buildPayload(post));
      setPost(res.data);
      setAutoSavedAt(new Date());
      setDirty(false);
      if (!silent) toast.success("Saved");
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [post, id, buildPayload]);

  // Autosave: 2.5s after last change
  useEffect(() => {
    if (!dirty || !post) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => saveNow(true), AUTOSAVE_DELAY_MS);
    return () => clearTimeout(autosaveTimer.current);
  }, [post, dirty, saveNow]);

  const patch = (k, v) => { setPost((prev) => ({ ...prev, [k]: v })); setDirty(true); };

  const togglePublish = async () => {
    const ep = post.status === "published" ? "unpublish" : "publish";
    await axios.post(`${API}/cms/blog/${id}/${ep}`);
    const res = await axios.get(`${API}/cms/blog/${id}`);
    setPost(res.data);
    toast.success(post.status === "published" ? "Unpublished" : "Published");
  };

  const openMedia = (cb) => { setMediaOnPick(() => cb); setShowMediaPicker(true); };

  const doSchedule = async () => {
    if (!scheduleAt) return toast.error("Pick a date and time");
    // datetime-local returns local time — convert to UTC ISO
    const whenUtc = new Date(scheduleAt).toISOString();
    try {
      await axios.post(`${API}/cms/blog/${id}/schedule`, { when: whenUtc });
      const res = await axios.get(`${API}/cms/blog/${id}`);
      setPost(res.data);
      toast.success("Scheduled");
      setShowSchedule(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Schedule failed");
    }
  };

  const doUnschedule = async () => {
    await axios.post(`${API}/cms/blog/${id}/unschedule`);
    const res = await axios.get(`${API}/cms/blog/${id}`);
    setPost(res.data);
    toast.success("Back to draft");
  };

  const loadRevisions = async () => {
    const r = await axios.get(`${API}/cms/blog/${id}/revisions`);
    setRevisions(r.data);
    setShowRevisions(true);
  };

  const restoreRevision = async (revId) => {
    if (!window.confirm("Restore this revision? Current state will be snapshotted first.")) return;
    await axios.post(`${API}/cms/blog/${id}/revisions/${revId}/restore`);
    const res = await axios.get(`${API}/cms/blog/${id}`);
    setPost(res.data);
    setShowRevisions(false);
    toast.success("Revision restored");
  };

  if (!post) return <div className="text-slate-400 p-6">Loading…</div>;

  const toggleInArray = (field, value) => {
    const arr = post[field] || [];
    const next = arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
    patch(field, next);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-16">
      <header className="flex items-center justify-between flex-wrap gap-3 sticky top-0 bg-slate-50/90 backdrop-blur z-10 py-3 -mx-4 px-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <Link to="/app/website/blog" className="text-slate-500 hover:text-slate-900 p-1"><ArrowLeft size={18} /></Link>
          <div>
            <h1 className="text-xl font-bold text-slate-900 truncate max-w-md">{post.title || "Untitled"}</h1>
            <p className="text-xs text-slate-500 flex items-center gap-2">
              <Badge status={post.status} />
              <code className="bg-slate-100 px-1.5 py-0.5 rounded">/{post.slug}</code>
              {autoSavedAt && <span className="text-prepify-600">✓ Autosaved {autoSavedAt.toLocaleTimeString()}</span>}
              {dirty && !saving && <span className="text-amber-600">Unsaved changes…</span>}
              {saving && <span className="text-slate-500">Saving…</span>}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {post.status === "published" && (
            <a href={`${BASE}/blog/${post.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium px-3 py-2 rounded-lg text-sm">
              <ExternalLink size={14} /> View live
            </a>
          )}
          <button onClick={() => nav(`/app/website/blog/preview/${id}`)} className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium px-3 py-2 rounded-lg text-sm" data-testid="preview-post-btn">
            <Eye size={14} /> Preview
          </button>
          <button onClick={loadRevisions} className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium px-3 py-2 rounded-lg text-sm" data-testid="revisions-btn">
            <History size={14} /> Revisions
          </button>
          {post.status !== "scheduled" ? (
            <button onClick={() => setShowSchedule(true)} className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium px-3 py-2 rounded-lg text-sm" data-testid="schedule-btn">
              <Calendar size={14} /> Schedule
            </button>
          ) : (
            <button onClick={doUnschedule} className="inline-flex items-center gap-2 border border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-700 font-medium px-3 py-2 rounded-lg text-sm" data-testid="unschedule-btn">
              <Calendar size={14} /> Unschedule
            </button>
          )}
          <button onClick={togglePublish} className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium px-3 py-2 rounded-lg text-sm" data-testid="toggle-publish-btn">
            {post.status === "published" ? <><EyeOff size={14} /> Unpublish</> : <><Eye size={14} /> Publish</>}
          </button>
          <button onClick={() => saveNow()} disabled={saving} className="inline-flex items-center gap-2 bg-slate-900 hover:bg-prepify-600 text-white font-medium px-4 py-2 rounded-lg disabled:opacity-50 text-sm" data-testid="save-post-btn">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
            <F label="Title"><I value={post.title || ""} onChange={(e) => patch("title", e.target.value)} placeholder="Post title" data-testid="post-title-input" /></F>
            <F label="Slug" hint="Auto-generated from title if left blank"><I value={post.slug || ""} onChange={(e) => patch("slug", e.target.value)} placeholder="url-friendly-slug" data-testid="post-slug-input" /></F>
            <F label="Excerpt" hint="Short summary shown in list view"><TA rows={2} value={post.excerpt || ""} onChange={(e) => patch("excerpt", e.target.value)} data-testid="post-excerpt-input" /></F>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="px-6 pt-5 pb-3">
              <span className="text-sm font-medium text-slate-700">Content</span>
              <p className="text-xs text-slate-500 mt-0.5">Use the toolbar for formatting. Click the image button to insert from the media library. Toggle HTML mode for raw editing.</p>
            </div>
            <div className="px-6 pb-6">
              <RichTextEditor
                value={post.content_html || ""}
                onChange={(html) => patch("content_html", html)}
                onOpenMediaPicker={openMedia}
                placeholder="Start writing your post…"
              />
            </div>
          </section>
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <section className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">Featured image</h3>
            {post.cover_image_url ? (
              <div className="relative group">
                <img src={post.cover_image_url.startsWith("/") ? `${BASE}${post.cover_image_url}` : post.cover_image_url} alt="cover" className="w-full rounded-lg border border-slate-200" />
                <button onClick={() => patch("cover_image_url", "")} className="absolute top-2 right-2 bg-white/90 hover:bg-red-50 hover:text-red-600 p-1.5 rounded-full shadow" data-testid="remove-cover-btn"><X size={14} /></button>
              </div>
            ) : (
              <button onClick={() => openMedia((url) => patch("cover_image_url", url))} className="w-full border-2 border-dashed border-slate-200 hover:border-prepify-400 hover:bg-prepify-50/30 rounded-lg py-8 flex flex-col items-center gap-2 text-slate-500 transition" data-testid="pick-cover-btn">
                <ImageIcon size={28} />
                <span className="text-xs">Pick from library</span>
              </button>
            )}
            {post.cover_image_url && (
              <button onClick={() => openMedia((url) => patch("cover_image_url", url))} className="text-xs text-prepify-700 hover:underline">Change image</button>
            )}
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-900">Organize</h3>
            <F label="Categories">
              {categories.length === 0 ? (
                <p className="text-xs text-slate-400">No categories yet. <Link to="/app/website/blog-categories" className="text-prepify-700 hover:underline">Create one</Link>.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {categories.map((c) => {
                    const active = (post.category_ids || []).includes(c.id);
                    return (
                      <button
                        key={c.id}
                        onClick={() => toggleInArray("category_ids", c.id)}
                        className={`text-xs font-medium px-2.5 py-1 rounded-full border transition ${active ? "text-white border-transparent" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
                        style={active ? { background: c.color || "#0057D9" } : {}}
                        data-testid={`category-chip-${c.slug}`}
                      >{c.name}</button>
                    );
                  })}
                </div>
              )}
            </F>
            <F label="Tags">
              {tags.length === 0 ? (
                <p className="text-xs text-slate-400">No tags yet. <Link to="/app/website/blog-tags" className="text-prepify-700 hover:underline">Create one</Link>.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {tags.map((t) => {
                    const active = (post.tag_ids || []).includes(t.id);
                    return (
                      <button
                        key={t.id}
                        onClick={() => toggleInArray("tag_ids", t.id)}
                        className={`text-xs px-2.5 py-1 rounded-full border transition ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
                        data-testid={`tag-chip-${t.slug}`}
                      >#{t.name}</button>
                    );
                  })}
                </div>
              )}
            </F>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">Author</h3>
            <F label="Display as (override)" hint="Leave blank to use the selected author's name">
              <I value={post.author_override_name || ""} onChange={(e) => patch("author_override_name", e.target.value)} placeholder="e.g. Guest writer name" data-testid="author-override-input" />
            </F>
            <F label="Select from staff">
              <select
                value={post.author_id || ""}
                onChange={(e) => {
                  const u = users.find((x) => x.id === e.target.value);
                  setPost((prev) => ({ ...prev, author_id: e.target.value, author_name: u?.name || "" }));
                  setDirty(true);
                }}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-prepify-500/40"
                data-testid="author-select"
              >
                <option value="">— Auto (me) —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
            </F>
            <p className="text-xs text-slate-500">Current: <b>{post.author_override_name || post.author_name || "—"}</b></p>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">SEO</h3>
            <F label="SEO Title" hint="Defaults to post title"><I value={post.seo_title || ""} onChange={(e) => patch("seo_title", e.target.value)} /></F>
            <F label="Meta Description"><TA rows={3} value={post.seo_description || ""} onChange={(e) => patch("seo_description", e.target.value)} /></F>
            <F label="OG Image">
              <div className="flex gap-2">
                <I value={post.seo_og_image || ""} onChange={(e) => patch("seo_og_image", e.target.value)} className="flex-1" placeholder="URL or pick" />
                <button onClick={() => openMedia((url) => patch("seo_og_image", url))} className="px-2 border border-slate-200 rounded-lg hover:bg-slate-50"><ImageIcon size={14} /></button>
              </div>
            </F>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">Info</h3>
            <p className="text-xs text-slate-500">Views: {post.views || 0}</p>
            <p className="text-xs text-slate-500">Created: {new Date(post.created_at).toLocaleString()}</p>
            <p className="text-xs text-slate-500">Updated: {new Date(post.updated_at).toLocaleString()}</p>
            {post.published_at && <p className="text-xs text-slate-500">Published: {new Date(post.published_at).toLocaleString()}</p>}
            {post.scheduled_publish_at && <p className="text-xs text-amber-600 font-medium">Scheduled: {new Date(post.scheduled_publish_at).toLocaleString()}</p>}
          </section>
        </aside>
      </div>

      {/* Schedule modal */}
      {showSchedule && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={() => setShowSchedule(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Schedule publish</h3>
            <p className="text-sm text-slate-500 mb-4">Choose when this post should go live. Stored as UTC on the server.</p>
            <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="schedule-datetime" />
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowSchedule(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button onClick={doSchedule} className="px-4 py-2 text-sm bg-prepify-600 hover:bg-prepify-700 text-white rounded-lg font-medium" data-testid="schedule-confirm-btn">Schedule</button>
            </div>
          </div>
        </div>
      )}

      {/* Revisions drawer */}
      {showRevisions && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={() => setShowRevisions(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <header className="flex items-center justify-between p-5 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Revision history</h3>
                <p className="text-xs text-slate-500">Last {revisions.length} saves — restore any version</p>
              </div>
              <button onClick={() => setShowRevisions(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X size={16} /></button>
            </header>
            <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
              {revisions.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-400">No revisions yet. Edit and save to create one.</div>
              ) : (
                revisions.map((r) => (
                  <div key={r.id} className="flex items-center justify-between p-4 hover:bg-slate-50">
                    <div>
                      <div className="text-sm font-medium text-slate-800">{new Date(r.created_at).toLocaleString()}</div>
                      <div className="text-xs text-slate-500">by {r.created_by_name || "—"} {r.note && <span>· {r.note}</span>}</div>
                    </div>
                    <button onClick={() => restoreRevision(r.id)} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-prepify-50 hover:border-prepify-300 hover:text-prepify-700" data-testid={`restore-rev-${r.id}`}>
                      <RotateCcw size={12} /> Restore
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <MediaPicker
        open={showMediaPicker}
        onClose={() => { setShowMediaPicker(false); setMediaOnPick(null); }}
        onSelect={(url) => { mediaOnPick?.(url); }}
      />
    </div>
  );
};

export default BlogEditor;
